/**
 * useBlogData
 *
 * Fetches blog posts from the CDN /blogs/ directory.
 *
 * Called without a slug → fetches and returns all blog posts (listing page).
 * Called with a slug    → fetches a single blog post by slug (detail page).
 *
 * Usage:
 *   const { blogs, loading, error } = useBlogData();
 *   const { blog, loading, error } = useBlogData('eur-not-currency');
 *
 * Returns:
 * {
 *   loading: boolean,
 *   error: string | null,
 *   blogs: BlogPost[],       // populated when called without slug
 *   blog: BlogPost | null,   // populated when called with slug
 * }
 *
 * BlogPost shape (all CDN-resolved, no external links needed):
 * {
 *   id, title, subtitle, date, categories, readTime, excerpt,
 *   hasPdf, sections, coverImage, pdfLink
 * }
 */

import { useState, useEffect } from 'react';

import { getBlogs, getBlogBySlug } from '../services/cmsService';

const useBlogData = (slug = null) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [blog, setBlog] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        if (slug) {
          const data = await getBlogBySlug(slug);
          if (!cancelled) setBlog(data);
        } else {
          const data = await getBlogs();
          if (!cancelled) setBlogs(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load blog data');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { blogs, blog, loading, error };
};

export default useBlogData;
